import React, { useEffect, useState, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { ArrowLeft, Flame, SkipForward, Download } from "lucide-react";
import {
  startChat,
  sendMessage,
  switchMode,
  endChat,
  nextCase,
  getTranscript,
} from "../lib/api";
import ModeSwitcher from "../components/ModeSwitcher";
import VitalsMonitor from "../components/VitalsMonitor";
import ChatPanel from "../components/ChatPanel";
import { toast, Toaster } from "sonner";
import { useFeatureTimer } from "../lib/analytics";

const ACTIVITY_LABEL = {
  daily_case: "Daily Case Scenario",
  simulation_drill: "Simulation Drill",
  understand_concept: "Understand a Concept",
  // legacy fallbacks so already-open sessions still render a header title
  best_preceptor: "Understand a Concept",
  deep_dive: "Understand a Concept",
};

export default function Simulation() {
  useFeatureTimer("simulation");
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const activity = params.get("activity") || "daily_case";
  const initialMode = params.get("mode") || "standard";

  const [mode, setMode] = useState(initialMode);
  const [sessionId, setSessionId] = useState(null);
  const [scenario, setScenario] = useState(null);
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [starting, setStarting] = useState(true);
  const [streak, setStreak] = useState(0);

  const isDaily = activity === "daily_case";
  const showMonitor = activity === "daily_case" || activity === "simulation_drill";

  const bootstrap = useCallback(async () => {
    setStarting(true);
    try {
      const data = await startChat({ activity, mode: initialMode });
      setSessionId(data.session_id);
      setScenario(data.scenario || null);
      setStreak(data.streak || 0);
      setMessages([{ role: "assistant", content: data.opening_message }]);
    } catch (e) {
      toast.error("Could not start session", { description: e?.response?.data?.detail || e.message });
    } finally {
      setStarting(false);
    }
  }, [activity, initialMode]);

  useEffect(() => {
    bootstrap();
    return () => {
      if (sessionId) endChat(sessionId).catch(() => {});
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const recoverSession = async () => {
    const data = await startChat({ activity, mode });
    setSessionId(data.session_id);
    if (data.scenario) setScenario(data.scenario);
    setStreak(data.streak || 0);
    return data.session_id;
  };

  const onSend = async (text) => {
    if (!sessionId) return;
    setMessages((m) => [...m, { role: "user", content: text }]);
    setLoading(true);
    try {
      const data = await sendMessage(sessionId, text);
      setMessages((m) => [...m, { role: "assistant", content: data.reply }]);
      if (typeof data.streak === "number") setStreak(data.streak);
      if (data.scenario) setScenario(data.scenario);
    } catch (e) {
      if (e?.response?.status === 404) {
        try {
          toast.info("Session reset", { description: "Reconnecting…" });
          const newId = await recoverSession();
          const data = await sendMessage(newId, text);
          setMessages((m) => [...m, { role: "assistant", content: data.reply }]);
          if (typeof data.streak === "number") setStreak(data.streak);
        } catch (err) {
          toast.error("Could not reconnect");
        }
      } else {
        toast.error("Message failed", { description: e?.response?.data?.detail || e.message });
      }
    } finally {
      setLoading(false);
    }
  };

  const onModeChange = async (newMode) => {
    if (newMode === mode || !sessionId) return;
    setMode(newMode);
    setLoading(true);
    try {
      const data = await switchMode(sessionId, newMode);
      setMessages((m) => [...m, { role: "assistant", content: data.reply }]);
    } catch (e) {
      if (e?.response?.status === 404) {
        try { await recoverSession(); } catch (err) { console.error("recoverSession failed", err); }
      } else {
        toast.error("Mode switch failed");
      }
    } finally {
      setLoading(false);
    }
  };

  const onNextCase = async () => {
    if (!sessionId || !isDaily) return;
    setLoading(true);
    setMessages((m) => [...m, { role: "user", content: "next" }]);
    try {
      const data = await nextCase(sessionId);
      setMessages((m) => [...m, { role: "assistant", content: data.reply }]);
      setStreak(data.streak || 0);
      if (data.scenario) setScenario(data.scenario);
    } catch (e) {
      if (e?.response?.status === 404) {
        try { await recoverSession(); } catch (err) { console.error("recoverSession failed", err); }
      } else {
        toast.error("Could not load next case");
      }
    } finally {
      setLoading(false);
    }
  };

  const onExport = async () => {
    if (!sessionId) return;
    try {
      const text = await getTranscript(sessionId);
      const blob = new Blob([text], { type: "text/plain;charset=utf-8" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      const stamp = new Date().toISOString().slice(0, 16).replace(/[:T]/g, "-");
      a.download = `project-rn-${activity}-${stamp}.txt`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      toast.success("Transcript downloaded");
    } catch (e) {
      toast.error("Export failed");
    }
  };

  return (
    <div className="min-h-screen bg-[#f1eee7] flex flex-col">
      <Toaster position="top-right" />
      <header className="border-b border-[#e4ded2] bg-[#fbf8f2]">
        <div className="max-w-7xl mx-auto px-6 py-3.5 flex items-center justify-between gap-4">
          <div className="flex items-center gap-4 min-w-0">
            <button
              onClick={() => navigate("/")}
              data-testid="back-btn"
              className="flex items-center gap-1.5 text-xs uppercase tracking-widest text-[#6b6258] hover:text-[#6f2a30] transition-colors shrink-0"
            >
              <ArrowLeft className="h-3.5 w-3.5" />
              Back
            </button>
            <div className="h-5 w-px bg-[#e4ded2] shrink-0"></div>
            <div className="flex items-center gap-2 min-w-0">
              <img
                src="/project-rn-logo-horizontal.png"
                alt="Project RN"
                data-testid="app-logo"
                className="h-7 w-auto shrink-0"
              />
              <div className="h-5 w-px bg-[#e4ded2] shrink-0" />
              <div className="min-w-0">
                <div className="text-[9px] uppercase tracking-[0.2em] text-[#6b6258]">Simulation</div>
                <div className="text-sm font-semibold tracking-tight text-[#1a1612] truncate">
                  {ACTIVITY_LABEL[activity] || "Simulation"}
                </div>
              </div>
            </div>
            {isDaily && (
              <div
                className="hidden sm:flex items-center gap-1.5 px-2.5 py-1 bg-[#6f2a30] text-[#f1eee7] rounded-sm"
                data-testid="streak-counter"
              >
                <Flame className="h-3.5 w-3.5 text-[#d62828]" strokeWidth={1.8} />
                <span className="text-[10px] uppercase tracking-widest font-medium">Streak</span>
                <span className="font-mono text-sm font-semibold">{streak}</span>
              </div>
            )}
          </div>

          <div className="flex items-center gap-2 shrink-0">
            {isDaily && (
              <ModeSwitcher mode={mode} onChange={onModeChange} disabled={loading || starting} />
            )}
            {isDaily && (
              <button
                onClick={onNextCase}
                disabled={loading || starting}
                data-testid="next-case-btn"
                title="Load the next scenario"
                className="hidden md:flex items-center gap-1.5 px-3 py-1.5 bg-[#6f2a30] text-[#f1eee7] text-xs uppercase tracking-widest font-medium rounded-sm hover:bg-[#582227] transition-colors disabled:opacity-50"
              >
                <SkipForward className="h-3.5 w-3.5" strokeWidth={1.8} />
                Next
              </button>
            )}
            <button
              onClick={onExport}
              disabled={starting}
              data-testid="export-btn"
              title="Download transcript"
              className="flex items-center gap-1.5 px-3 py-1.5 border border-[#e4ded2] text-[#1a1612] text-xs uppercase tracking-widest font-medium rounded-sm hover:bg-[#6f2a30] hover:text-[#f1eee7] hover:border-[#6f2a30] transition-colors disabled:opacity-50"
            >
              <Download className="h-3.5 w-3.5" strokeWidth={1.8} />
              <span className="hidden lg:inline">Export</span>
            </button>
          </div>
        </div>
      </header>

      <main className="flex-1 max-w-7xl w-full mx-auto px-6 py-6">
        <div className={`grid grid-cols-1 gap-5 h-[calc(100vh-130px)] ${showMonitor ? "lg:grid-cols-12" : ""}`}>
          <div className={`min-h-[60vh] lg:min-h-0 ${showMonitor ? "lg:col-span-8" : ""}`}>
            {starting ? (
              <div className="h-full grid place-items-center text-[#6b6258] text-sm border border-[#e4ded2] bg-[#fbf8f2] rounded-sm">
                <span className="font-mono">Initializing session…</span>
              </div>
            ) : (
              <ChatPanel
                messages={messages}
                onSend={onSend}
                loading={loading}
                suggestQuick={isDaily}
              />
            )}
          </div>
          {showMonitor && (
            <div className="lg:col-span-4">
              <VitalsMonitor scenario={scenario} />
            </div>
          )}
        </div>
      </main>
    </div>
  );
}
